import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RegistryCategory, ExplorerItem, ViewMode } from './types';
import { CompactRow } from './CompactRow';
import { Card } from '../../../../../components/ui/Card';
interface CategorySectionProps {
  category: RegistryCategory;
  items: ExplorerItem[];
  viewMode: ViewMode;
  onSelectItem: (id: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  accentColor?: 'blue' | 'purple';
  renderCardContent?: (item: ExplorerItem) => React.ReactNode;
}
export function CategorySection({
  category,
  items,
  viewMode,
  onSelectItem,
  isExpanded,
  onToggleExpanded,
  accentColor = 'blue',
  renderCardContent
}: CategorySectionProps) {
  const Icon = category.icon;
  if (items.length === 0) return null;
  const activeCount = items.filter(i => i.usageCount > 0).length;
  const accentClass = accentColor === 'blue' ? 'text-blue-600' : 'text-purple-600';
  const bgAccentClass = accentColor === 'blue' ? 'bg-blue-50' : 'bg-purple-50';
  return <div className="mb-4 sm:mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6156" data-mp="26zvbi9p0s3uy">
      <button onClick={onToggleExpanded} className="w-full px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-50 transition-colors border-b border-gray-100 gap-3" data-id="element-6157" data-mp="220io9nu8kuru">
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1" data-id="element-6158" data-mp="2uppmelps3ick">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${bgAccentClass} ${accentClass}`} data-id="element-6159" data-mp="2pqmgkz1z583j">
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-6160" data-mp="2lmy90gdcecia" />
          </div>
          <div className="text-left min-w-0" data-id="element-6161" data-mp="2gowodr09q54p">
            <h4 className="font-bold text-sm sm:text-base text-gray-900 truncate" data-id="element-6162" data-mp="2gs13hjf9ula5">
              {category.label}
            </h4>
            <p className="text-[11px] sm:text-xs text-gray-500 truncate" data-id="element-6163" data-mp="15a9pc4p4pn31">
              <span className="sm:hidden" data-id="element-6164" data-mp="16ros10mgheo8">
                {activeCount}/{items.length} actief
              </span>
              <span className="hidden sm:inline" data-id="element-6165" data-mp="1brb7zhto590q">{category.description}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-6166" data-mp="xpyt79q8vdgf">
          <div className="text-right hidden sm:block" data-id="element-6167" data-mp="11sby5vx637c5">
            <div className="text-sm font-medium text-gray-900" data-id="element-6168" data-mp="11qsfxzryam7q">
              {activeCount} / {items.length} actief
            </div>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" data-id="element-6169" data-mp="ssp5pzmf1bv8" /> : <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" data-id="element-6170" data-mp="e42g9532r3qa" />}
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-6171" data-mp="j2vrequw2ina">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-6172" data-mp="51t8gmz6xogl">
            <div className="p-3 sm:p-5" data-id="element-6173" data-mp="3vmri66ivb9cv">
              {viewMode === 'compact' ? <div className="flex flex-col gap-1.5 sm:gap-2" data-id="element-6174" data-mp="3rjld327hro7z">
                  {items.map(item => <CompactRow key={item.id} item={item} onClick={() => onSelectItem(item.id)} accentColor={accentColor} data-id="element-6175" data-mp="9dqdk9znzkiv" />)}
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-id="element-6176" data-mp="e4yiottyojq8">
                  {items.map(item => {
              const ItemIcon = item.icon;
              const isActive = item.usageCount > 0;
              const hoverClass = accentColor === 'blue' ? 'hover:border-blue-300' : 'hover:border-purple-300';
              return <Card key={item.id} className={`p-3 sm:p-4 h-full flex flex-col transition-all duration-200 ${isActive ? `cursor-pointer group relative ${hoverClass} border-gray-200 hover:shadow-md` : 'opacity-50 border-gray-100 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isActive && onSelectItem(item.id)} data-id="element-6177" data-mp="2rc6kiqnky8l7">
                        <div className="flex items-start justify-between mb-2.5 sm:mb-3" data-id="element-6178" data-mp="27no1yrx2xtfh">
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${isActive ? `${bgAccentClass} ${accentClass} group-hover:scale-105 transition-transform` : 'bg-gray-200 text-gray-400'}`} data-id="element-6179" data-mp="2cnjv1w48o0g9">
                            <ItemIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6180" data-mp="20gjpmjt7byw" />
                          </div>
                          <div className="text-right" data-id="element-6181" data-mp="3t6j21a60md74">
                            <div className={`text-base sm:text-lg font-black ${isActive ? accentClass : 'text-gray-400'}`} data-id="element-6182" data-mp="3t7t1a2oltrfc">
                              {item.usageCount}
                            </div>
                            <div className="text-[10px] text-gray-500 font-medium" data-id="element-6183" data-mp="3fgnyirpxwj0s">
                              refs
                            </div>
                          </div>
                        </div>

                        <div className="mb-2.5 sm:mb-3 min-w-0" data-id="element-6184" data-mp="35i8aixm7lpvi">
                          <div className="flex items-center gap-2 mb-1 min-w-0" data-id="element-6185" data-mp="31gd6hlcw8ye1">
                            <code className="text-xs sm:text-sm font-mono font-bold text-gray-900 truncate" data-id="element-6186" data-mp="13q3mvybqteql">
                              {item.id}
                            </code>
                            {!item.isRegistered && <span className="text-[8px] bg-red-100 text-red-700 px-1 py-0.5 rounded font-bold uppercase tracking-wider shrink-0" data-id="element-6187" data-mp="2t6628x4x1d2a">
                                Unreg
                              </span>}
                          </div>
                          <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-2" title={item.description} data-id="element-6188" data-mp="2o7cr3bd3py5a">
                            {item.description}
                          </p>
                        </div>

                        {renderCardContent && isActive && <div className="mt-auto pt-2.5 sm:pt-3 border-t border-gray-100" data-id="element-6189" data-mp="37w3nb47jtmn9">
                            {renderCardContent(item)}
                          </div>}

                        {!renderCardContent && isActive && <div className="mt-auto pt-2.5 sm:pt-3 border-t border-gray-100" data-id="element-6190" data-mp="1wi9lns9hahr2">
                            <div className="flex flex-wrap gap-1" data-id="element-6191" data-mp="1nyd1n3jeknhh">
                              {Array.from(item.modules).sort().slice(0, 3).map(m => <span key={m} className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-gray-50 border-gray-200 text-gray-600" data-id="element-6192" data-mp="1syixyx0may5w">
                                    {m}
                                  </span>)}
                              {item.modules.size > 3 && <span className="text-[10px] text-gray-400 font-medium px-1 py-0.5" data-id="element-6193" data-mp="1a7rhzcm4k9ij">
                                  +{item.modules.size - 3}
                                </span>}
                            </div>
                          </div>}
                      </Card>;
            })}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}